import React, { useState, useEffect, useCallback } from 'react';
import { showAppAlert } from '../utils/alertService';
import EditPromotionModal from './EditPromotionModal';
import AddPromotionModal from './AddPromotionModal';
import LoaderOverlay from './LoaderOverlay';
import '../styles/formStyles.css';

const API_URL = process.env.REACT_APP_API_BASE_URL;

function PromoList() {
  const [promos, setPromos] = useState([]);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedPromo, setSelectedPromo] = useState(null);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const token = localStorage.getItem('token');
  const [isDeleting, setIsDeleting] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'nome', direction: 'asc' });
  const [confirmation, setConfirmation] = useState({
    isOpen: false,
    message: '',
    onConfirm: null,
    onCancel: null,
  });
  const [expandedDescId, setExpandedDescId] = useState(null);

  const fetchPromotionsData = useCallback(async () => {
    try {
      const companyId = localStorage.getItem('company_id');
      const response = await fetch(`${API_URL}/promotions?company_id=${companyId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        cache: 'no-cache', // Evita que o navegador use uma resposta antiga.
      });
      if (response.ok) {
        const data = await response.json();
        setPromos(data);
        setErrorMessage('');
      } else {
        const errorData = await response.json();
        setPromos([]);
        setErrorMessage(
          errorData.message ||
          errorData.error ||
          'Não foi possível buscar dados das promoções'
        );
      }
    } catch (error) {
      setPromos([]);
      setErrorMessage('Não foi possível buscar dados das promoções');
    }
  }, [token]);

  // Função para buscar dados das promoções da API
  useEffect(() => {
    fetchPromotionsData();
  }, [fetchPromotionsData]);

  // Função para ordenar promoções
  const sortPromos = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });

    const sortedPromos = [...promos].sort((a, b) => {
      if (a.status !== b.status) {
        return b.status - a.status;
      }

      // Ordena pelo campo selecionado
      if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
      if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    setPromos(sortedPromos);
  };

  // Função para obter o ícone de ordenação
  const getSortIcon = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'asc' ? '▲' : '▼';
    }
    return '';
  };

  // Função para formatar data para o padrão DD-MM-AAAA
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    // Adiciona 'T00:00:00' para evitar problemas de fuso horário com datas no formato YYYY-MM-DD
    const date = new Date(dateString.includes('T') ? dateString : `${dateString}T00:00:00`);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Mês é base 0
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const handleEditPromotion = (promo) => {
    setSelectedPromo(promo);
    setEditModalVisible(true);
  };

  const handleDeletePromotion = async (promo) => {
    setConfirmation({
      isOpen: true,
      message: 'Tem certeza que deseja excluir esta promoção?',
      onConfirm: () => {
        proceedWithDelete(promo);
        setConfirmation({ isOpen: false });
      },
      onCancel: () => setConfirmation({ isOpen: false }),
    });
  };

  const proceedWithDelete = async (promo) => {
    setIsDeleting(true);
    try {
      const response = await fetch(`${API_URL}/promotions/${promo.id}`, {
        method: 'DELETE',
        headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      });

      if (response.ok) {
        showAppAlert('success', 'Promoção excluída com sucesso!');
        fetchPromotionsData();
      } else {
        const errorData = await response.json();
        showAppAlert('error', errorData.message || 'Ocorreu um erro ao excluir a promoção.');
      }
    } catch (error) {
      console.error('Erro ao excluir promoção:', error);
      showAppAlert('error', `Erro de conexão ao tentar excluir a promoção: ${error.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCloseEditModal = () => {
    setEditModalVisible(false);
    setSelectedPromo(null);
  };

  const handleUpdatePromotion = () => {
    // Recarrega a lista de promoções da API para refletir a atualização.
    fetchPromotionsData();
    handleCloseEditModal();
  };

  const handleOpenAddModal = () => {
    setAddModalVisible(true);
  };

  const handleCloseAddModal = () => {
    setAddModalVisible(false);
  };

  const handleAddPromotion = () => {
    // Recarrega a lista de promoções da API para incluir a nova promoção.
    fetchPromotionsData();
    handleCloseAddModal();
  };

  // Componente para renderizar a célula de descrição com "Ver mais/menos"
  const DescriptionCell = ({ promo }) => {
    const isExpanded = expandedDescId === promo.id;
    const isLongText = promo.descricao && promo.descricao.length > 10;

    const toggleDescription = () => {
      setExpandedDescId(isExpanded ? null : promo.id);
    };

    return (
      <td>
        {isLongText && !isExpanded
          ? `${promo.descricao.substring(0, 10)}...`
          : promo.descricao}
        {isLongText && (
          <button onClick={toggleDescription} className="link-button" style={{ marginLeft: '5px', background: 'none', border: 'none', color: 'blue', cursor: 'pointer', padding: 0 }}>
            {isExpanded ? 'ver menos' : 'ver mais'}
          </button>
        )}
      </td>
    );
  };

  return (
    <div>
      <LoaderOverlay isLoading={isDeleting} message="Excluindo promoção..." />
      <table className="dashboard-table">
        <thead>
          <tr>
            <th onClick={() => sortPromos('nome')}>
              Nome {getSortIcon('nome')}
            </th>
            <th onClick={() => sortPromos('descricao')}>
              Descrição {getSortIcon('descricao')}
            </th>

            <th onClick={() => sortPromos('data_inicio')}>
              Data de Início {getSortIcon('data_inicio')}
            </th>
            
            <th onClick={() => sortPromos('data_termino')}>
              Data de Término {getSortIcon('data_termino')}
            </th>

            <th onClick={() => sortPromos('pontos')}>
              Pontos {getSortIcon('pontos')}
            </th>

            <th onClick={() => sortPromos('operations_count')}>
              Resgates {getSortIcon('operations_count')}
            </th>
            
            <th onClick={() => sortPromos('status')}>
              Status {getSortIcon('status')}
            </th>

            <th>Ações</th>
          </tr>
        </thead>
        <tbody>
          {promos.map((promo, index) => (
            <tr key={promo.id} className={index % 2 === 0 ? 'even-row' : 'odd-row'}>
              <td>{promo.nome}</td>
              <DescriptionCell promo={promo} />
              <td>{formatDate(promo.data_inicio)}</td>
              <td>{formatDate(promo.data_termino)}</td>
              <td>{promo.pontos}</td>
              <td>{promo.operations_count}</td>
              <td>{promo.status === 1 ? 'Ativo' : 'Inativo'}</td>
              <td>
                <div style={{ display: 'flex', gap: '5px' }}>
                  <button onClick={() => handleEditPromotion(promo)} title="Editar" className="action-button">
                    ✎
                  </button>
                  <button onClick={() => handleDeletePromotion(promo)} title="Excluir" className="action-button">
                    🗑
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {errorMessage && <p style={{ color: 'red', marginTop: '10px' }}>{errorMessage}</p>}
      <div style={{ marginTop: '10px', textAlign: 'right' }}>
        <button onClick={handleOpenAddModal} title="Adicionar Promoção" className="button-style">
          +
        </button>
      </div>
      <EditPromotionModal
        promotion={selectedPromo}
        visible={editModalVisible}
        onClose={handleCloseEditModal}
        onUpdate={handleUpdatePromotion}
      />
      <AddPromotionModal
        visible={addModalVisible}
        onClose={handleCloseAddModal}
        onAdd={handleAddPromotion}
      />

      {confirmation.isOpen && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: 350 }}>
            <h3>Confirmação</h3>
            <p style={{ margin: '20px 0' }}>{confirmation.message}</p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                className="button-style"
                onClick={confirmation.onConfirm}
              >
                Confirmar
              </button>
              <button
                className="button-style cancel"
                onClick={confirmation.onCancel}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PromoList;